import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const CollaborativeStatusBar = ({ 
  participants = [], 
  sessionStatus = 'active', 
  sessionId = '',
  currentUserId = null,
  onInviteParticipant = () => {},
  onViewParticipants = () => {}
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const defaultParticipants = [
    { id: '1', name: 'María García', avatar: null, status: 'active', isHost: true },
    { id: '2', name: 'Carlos López', avatar: null, status: 'active', isHost: false },
    { id: '3', name: 'Ana Martín', avatar: null, status: 'idle', isHost: false },
  ];

  const activeParticipants = participants?.length > 0 ? participants : defaultParticipants;
  const activeCount = activeParticipants?.filter(p => p?.status === 'active')?.length;

  // Helper para mostrar nombre con máscara "Tú"
  const getDisplayName = (participant) => {
    const isCurrentUser = currentUserId && String(participant?.id) === String(currentUserId);
    console.log('👤 getDisplayName called:', { 
      participantId: participant?.id, 
      participantName: participant?.name,
      currentUserId, 
      isCurrentUser,
      willShow: isCurrentUser ? 'Tú' : participant?.name
    });
    return isCurrentUser ? 'Tú' : participant?.name;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success';
      case 'idle':
        return 'bg-warning';
      case 'disconnected':
        return 'bg-error';
      default:
        return 'bg-muted';
    }
  };

  const getSessionStatusColor = () => {
    switch (sessionStatus) {
      case 'active':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'completed':
        return 'text-primary';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-surface border-b border-border">
      <div className="px-4 py-2">
        {/* Main Status Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Session Status */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${getStatusColor(sessionStatus)} animate-pulse-soft`} />
              <span className={`text-sm font-medium ${getSessionStatusColor()}`}>
                Sesión Activa
              </span>
            </div>

            {/* Participant Count - Desktop */}
            <div className="hidden sm:flex items-center space-x-2">
              <Icon name="Users" size={16} className="text-text-secondary" />
              <span className="text-sm text-text-secondary">
                {activeCount} de {activeParticipants?.length} conectados
              </span>
            </div>

            {/* Participant Avatars - Desktop */}
            <div className="hidden md:flex items-center -space-x-2">
              {activeParticipants?.slice(0, 4)?.map((participant) => (
                <div
                  key={participant?.id}
                  className="relative w-8 h-8 rounded-full bg-primary text-primary-foreground border-2 border-surface flex items-center justify-center text-xs font-medium"
                  title={getDisplayName(participant)}
                >
                  {currentUserId && String(participant?.id) === String(currentUserId) ? 'T' : getInitials(participant?.name)}
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border border-surface ${getStatusColor(participant?.status)}`}
                  />
                </div>
              ))}
              {activeParticipants?.length > 4 && (
                <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground border-2 border-surface flex items-center justify-center text-xs font-medium">
                  +{activeParticipants?.length - 4}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* Mobile Participant Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleExpanded}
              className="sm:hidden"
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconSize={16}
            >
              {activeCount}/{activeParticipants?.length}
            </Button>

            {/* Invite Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={onInviteParticipant}
              iconName="UserPlus"
              iconSize={14}
            >
              <span className="hidden sm:inline">Invitar</span>
            </Button>
          </div>
        </div>

        {/* Mobile Expanded Participant List */}
        {isExpanded && (
          <div className="sm:hidden mt-3 pt-3 border-t border-border">
            <div className="space-y-2">
              {activeParticipants?.map((participant) => (
                <div key={participant?.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="relative w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                      {getInitials(participant?.name)}
                      <div
                        className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-surface ${getStatusColor(participant?.status)}`}
                      />
                    </div>
                    <span className="text-sm text-foreground">
                      {getDisplayName(participant)}
                    </span>
                    {participant?.isHost && !(currentUserId && String(participant?.id) === String(currentUserId)) && (
                      <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                        Anfitrión
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-text-secondary capitalize">
                    {participant?.status === 'active' ? 'Activo' : 
                     participant?.status === 'idle' ? 'Inactivo' : 'Desconectado'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborativeStatusBar;